import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 * @property {HTMLInputElement} stickyQuantityInput
 * @property {HTMLButtonElement} quantityMinus
 * @property {HTMLButtonElement} quantityPlus
 * @property {HTMLSelectElement[]} [optionSelectors]
 * @property {HTMLElement} status
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  #variantGeneration = 0;
  #variantPending = false;
  #variantFailed = false;
  #cartPending = false;
  /** @type {HTMLElement | null} */
  #observedForm = null;
  #formObserver = new MutationObserver(() => this.#syncControls());

  connectedCallback() {
    super.connectedCallback();
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    window.addEventListener('resize', this.#updateScrollClearance, { signal });
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    this.#syncControls();
    customElements.whenDefined('quantity-selector-component').then(() => {
      if (!signal.aborted) this.#syncControls();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    this.#variantGeneration++;
    this.#variantPending = false;
    this.#formObserver.disconnect();
    this.#observedForm = null;
    document.documentElement.style.removeProperty('--avante-sticky-clearance');
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = () => {
    if (this.#variantPending || this.#variantFailed || this.#cartPending) return;
    this.#setQuantity(this.refs.stickyQuantityInput.value);
    this.#syncControls();
    if (!this.#targetAddToCartButton || this.refs.addToCartButton.disabled) return;
    this.#showStatus('');
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
  };

  /** @returns {Promise<void>} */
  #animateAdded = async () => {
    const { addToCartButton, productImage } = this.refs;
    addToCartButton.dataset.added = 'true';
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (cartIcon && productImage && customElements.get('fly-to-cart')) {
      const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
      flyToCartElement.classList.add('fly-to-cart--sticky');
      flyToCartElement.style.setProperty('background-image', `url(${productImage.src})`);
      flyToCartElement.useSourceSize = 'true';
      flyToCartElement.source = productImage;
      flyToCartElement.destination = cartIcon;
      document.body.appendChild(flyToCartElement);
      await onAnimationEnd([addToCartButton, flyToCartElement]);
    } else {
      await onAnimationEnd(addToCartButton);
    }

    if (!this.isConnected) return;
    this.#resetTimeout = setTimeout(() => addToCartButton.removeAttribute('data-added'), 800);
  };

  /** @param {Event} event @returns {void} */
  handleOptionChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    const picker = this.#getVariantPicker();
    const option = picker?.querySelector(`[data-option-value-id="${CSS.escape(select.value)}"]`);
    let target;
    if (option instanceof HTMLInputElement) {
      option.checked = true;
      target = option;
    } else if (option instanceof HTMLOptionElement && option.parentElement instanceof HTMLSelectElement) {
      option.parentElement.value = option.value;
      target = option.parentElement;
    }
    if (!target) {
      this.#variantFailed = true;
      this.#showStatus(this.dataset.unavailableText || '');
      this.#syncControls();
      return;
    }
    target.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /** @returns {void} */
  increaseQuantity = () => this.#stepQuantity(1);

  /** @returns {void} */
  decreaseQuantity = () => this.#stepQuantity(-1);

  /** @param {Event} event @returns {void} */
  handleQuantityChange = (event) => {
    if (event.target instanceof HTMLInputElement) this.#setQuantity(event.target.value);
  };

  /** @param {KeyboardEvent} event @returns {void} */
  handleQuantityKeydown = (event) => {
    if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement)) return;
    event.preventDefault();
    this.#setQuantity(event.target.value);
  };

  /** @param {number} direction @returns {void} */
  #stepQuantity(direction) {
    const source = this.#getQuantitySelector();
    if (!source?.updateQuantity || this.refs.stickyQuantityInput.disabled) return;
    this.#setQuantity(this.refs.stickyQuantityInput.value);
    source.updateQuantity(direction);
    this.#syncControls();
  }

  /** @param {string} value @returns {void} */
  #setQuantity(value) {
    const source = this.#getQuantitySelector();
    const input = source?.querySelector('input');
    if (!source?.setValue || !source.updateConstraints || !(input instanceof HTMLInputElement)) return;
    if (input.disabled || this.#variantPending) return;
    source.setValue(value);
    source.updateConstraints(input.min, input.max || null, input.step);
    source.onQuantityChange();
    this.refs.stickyQuantityInput.value = input.value;
    this.#syncControls();
  }

  /** @returns {import('@theme/component-quantity-selector').QuantitySelectorComponent | null} */
  #getQuantitySelector() {
    return this.#getProductForm()?.querySelector('quantity-selector-component') ?? null;
  }

  /** @returns {HTMLElement | null} */
  #getVariantPicker() {
    return this.closest('.shopify-section')?.querySelector(
      `variant-picker[data-product-id="${CSS.escape(this.dataset.productId || '')}"]:not(product-card variant-picker)`
    ) ?? null;
  }

  /** @param {string} message @returns {void} */
  #showStatus(message) {
    const status = this.querySelector('[ref="status"]');
    if (!(status instanceof HTMLElement)) return;
    status.textContent = message;
    status.hidden = !message;
  }

  /** @returns {void} */
  #syncControls() {
    if (!this.isConnected) return;
    const form = this.#getProductForm();
    if (form !== this.#observedForm) {
      this.#formObserver.disconnect();
      this.#observedForm = form;
      if (form) this.#formObserver.observe(form, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['min', 'max', 'step', 'disabled', 'data-cart-quantity', 'value'],
      });
    }
    this.#targetAddToCartButton = form?.querySelector('[ref="addToCartButton"]') ?? null;
    const source = this.#getQuantitySelector();
    const input = source?.querySelector('input');
    const quantityInput = this.querySelector('[ref="stickyQuantityInput"]');
    const minus = this.querySelector('[ref="quantityMinus"]');
    const plus = this.querySelector('[ref="quantityPlus"]');
    const button = this.querySelector('[ref="addToCartButton"]');
    const available = this.dataset.variantAvailable === 'true';
    const busy = this.#variantPending || this.#cartPending;
    if (quantityInput instanceof HTMLInputElement && minus instanceof HTMLButtonElement && plus instanceof HTMLButtonElement) {
      const disabled = !(input instanceof HTMLInputElement) || input.disabled || !available || this.#variantPending;
      quantityInput.disabled = disabled;
      minus.disabled = disabled;
      plus.disabled = disabled;
      if (input instanceof HTMLInputElement) {
        quantityInput.min = input.min;
        quantityInput.step = input.step;
        const max = source?.getEffectiveMax?.();
        if (max != null) quantityInput.max = String(max);
        else quantityInput.removeAttribute('max');
        if (document.activeElement !== quantityInput) quantityInput.value = input.value;
        this.#currentQuantity = Number(input.value) || Number(input.min) || 1;
        minus.disabled = disabled || this.#currentQuantity <= Number(input.min);
        plus.disabled = disabled || (max != null && this.#currentQuantity >= max);
      }
    }
    if (button instanceof HTMLButtonElement) {
      const canAdd = source?.canAddToCart?.().canAdd ?? true;
      button.disabled = !available || busy || this.#variantFailed || !this.#targetAddToCartButton ||
        this.#targetAddToCartButton.disabled || !canAdd;
      button.setAttribute('aria-busy', String(busy));
    }
    this.refs.stickyBar.setAttribute('aria-busy', String(this.#variantPending));
    this.#updateButtonText();
    this.#updateScrollClearance();
  }

  // Public action handlers
  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    const picker = this.#getVariantPicker();
    if (!(event.target instanceof Element) || event.target !== picker) return;
    const generation = ++this.#variantGeneration;
    this.#variantPending = true;
    this.#variantFailed = false;
    this.#showStatus('');
    this.#syncControls();

    event.promise
      .then(({ detail }) => {
        if (!this.isConnected || generation !== this.#variantGeneration) return;
        if (!detail?.html) throw new Error('Variant response missing HTML');
        const { html, resource: variant } = detail;
        const newSticky = html.querySelector('sticky-add-to-cart');
        const newBar = newSticky?.querySelector('[ref="stickyBar"]');
        if (!(newSticky instanceof HTMLElement) || !newBar) throw new Error('Variant response missing sticky bar');
        const focused = this.contains(document.activeElement) ? document.activeElement : null;
        const focusedId = focused instanceof HTMLElement ? focused.id : '';
        morph(this.refs.stickyBar, newBar, { childrenOnly: true });
        this.dataset.productId = newSticky.dataset.productId || this.dataset.productId;
        this.dataset.variantAvailable = newSticky.dataset.variantAvailable;
        this.dataset.currentVariantId = variant?.id ? String(variant.id) : '';
        if (variant == null) this.#handleVariantUnavailable();
        this.#variantPending = false;
        queueMicrotask(() => {
          if (!this.isConnected || generation !== this.#variantGeneration) return;
          this.#syncControls();
          if (focusedId && (!document.activeElement || document.activeElement === document.body)) {
            this.querySelector(`#${CSS.escape(focusedId)}`)?.focus({ preventScroll: true });
          }
        });
      })
      .catch(() => {
        if (!this.isConnected || generation !== this.#variantGeneration) return;
        this.#variantPending = false;
        this.#variantFailed = true;
        this.#showStatus(this.dataset.unavailableText || '');
        this.#syncControls();
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = this.#getVariantPicker();
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked, select option:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    const form = this.#getProductForm();
    if (!(event.target instanceof Element) || !form?.contains(event.target)) return;
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) this.#targetAddToCartButton.dataset.puppet = 'false';
      this.#cartPending = false;
      this.#syncControls();
    };
    if ('promise' in event && event.promise instanceof Promise) {
      this.#cartPending = true;
      this.#syncControls();
      event.promise.then(({ detail }) => {
        if (!this.isConnected) return;
        if (detail?.didError) {
          this.#showStatus(this.dataset.cartErrorText || '');
        } else {
          this.#showStatus('');
          this.#animateAdded();
        }
      }).catch(() => {
        if (this.isConnected) this.#showStatus(this.dataset.cartErrorText || '');
      }).finally(resetPuppet);
    } else {
      this.#showStatus(typeof event.detail?.description === 'string'
        ? event.detail.description : this.dataset.cartErrorText || '');
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine || !(event.target instanceof Element)) return;
    if (!this.#getProductForm()?.contains(event.target)) return;
    this.#syncControls();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    this.#updateScrollClearance();
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    this.#updateScrollClearance();
  }

  /** Keeps focused content clear without moving the observed footer. @returns {void} */
  #updateScrollClearance = () => {
    if (!this.isConnected) return;
    const { stickyBar } = this.refs;
    const bottom = Number.parseFloat(getComputedStyle(stickyBar).bottom) || 0;
    const clearance = this.#isStuck ? stickyBar.offsetHeight + bottom + 12 : 0;
    document.documentElement.style.setProperty('--avante-sticky-clearance', `${clearance}px`);
  };

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
